/**
 * Licence responses from the gateway's license middleware
 * (hyperion-be pkg/middleware/license.go).
 *
 * These are not authentication failures. Before this module only
 * `account_suspended` was recognised; `no_license` and
 * `license_state_invalid` fell into the auth-failure circuit, which signed
 * the user out repeatedly with no explanation, and a transient
 * `license_authority_unavailable` (503) surfaced as random page errors.
 */

import { toast } from 'sonner';

/** Codes that block the whole tenant until someone fixes the licence. */
export type LicenseBlockCode = 'account_suspended' | 'no_license' | 'license_state_invalid';

export type LicenseErrorKind =
  | { kind: 'blocked'; code: LicenseBlockCode }
  /** Licence server could not be reached — transient, retry; never a sign-out. */
  | { kind: 'unavailable' }
  /** Expired licence in read-only mode rejected a write; reads still work. */
  | { kind: 'read_only' };

const BLOCK_CODES: readonly LicenseBlockCode[] = [
  'account_suspended',
  'no_license',
  'license_state_invalid',
];

export const LICENSE_BLOCK_STORAGE_KEY = 'hyperion.license.block';
export const LICENSE_SUSPENDED_PATH = '/suspended';

export function classifyLicenseError(
  status: number | undefined,
  code: string | undefined
): LicenseErrorKind | null {
  if (!code) return null;
  if (status === 403 && (BLOCK_CODES as readonly string[]).includes(code)) {
    return { kind: 'blocked', code: code as LicenseBlockCode };
  }
  if (status === 503 && code === 'license_authority_unavailable') return { kind: 'unavailable' };
  if (status === 403 && code === 'license_expired') return { kind: 'read_only' };
  return null;
}

export interface StoredLicenseBlock {
  code: LicenseBlockCode;
  reason?: string;
}

export function readLicenseBlock(): StoredLicenseBlock | null {
  try {
    const raw = sessionStorage.getItem(LICENSE_BLOCK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredLicenseBlock>;
    if (!parsed.code || !(BLOCK_CODES as readonly string[]).includes(parsed.code)) return null;
    return {
      code: parsed.code,
      reason: typeof parsed.reason === 'string' && parsed.reason ? parsed.reason.slice(0, 200) : undefined,
    };
  } catch {
    return null;
  }
}

export function clearLicenseBlock(): void {
  try {
    sessionStorage.removeItem(LICENSE_BLOCK_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function storeLicenseBlock(block: StoredLicenseBlock): void {
  try {
    sessionStorage.setItem(LICENSE_BLOCK_STORAGE_KEY, JSON.stringify(block));
  } catch {
    /* the page falls back to a generic message */
  }
}

/**
 * Act on a licence response. Returns true when the response was a licence
 * outcome (the caller must then reject without counting it as an auth
 * failure and without refreshing or signing out).
 */
export function handleLicenseError(
  status: number | undefined,
  data: Record<string, unknown> | undefined,
  code: string | undefined
): boolean {
  const kind = classifyLicenseError(status, code);
  if (!kind) return false;

  if (kind.kind === 'blocked') {
    const reason = typeof data?.reason === 'string' ? data.reason : undefined;
    storeLicenseBlock({ code: kind.code, reason });
    if (window.location.pathname !== LICENSE_SUSPENDED_PATH) {
      window.location.href = LICENSE_SUSPENDED_PATH;
    }
    return true;
  }

  if (kind.kind === 'unavailable') {
    // One toast however many requests fail at once.
    toast.warning("Can't verify your Hyperion licence right now", {
      id: 'license-authority-unavailable',
      description: 'This is usually brief. You are still signed in — retry in a moment.',
      action: { label: 'Retry', onClick: () => window.location.reload() },
    });
    return true;
  }

  // read_only: the request's own error handling explains the rejected write.
  return true;
}
